/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */
import Keycloak from 'keycloak-js';
import KeycloakAuthorization from 'keycloak-js/dist/keycloak-authz';
import { parseJwt } from './auth.helper';

/**
 * Keys for saving tokens in local storage
 * @type {Readonly<{UMA_ACCESS_TOKEN: string, ACCESS_TOKEN: string, REFRESH_TOKEN: string, ID_TOKEN: string}>}
 */
const KeycloakStorageKeys = Object.freeze({
  UMA_ACCESS_TOKEN: 'fo:uma_access_token',
  UMA_ACCESS_TOKEN_PARSED: 'fo:uma_access_token_parsed',
  ACCESS_TOKEN: 'fo:access_token',
  ACCESS_TOKEN_PARSED: 'fo:access_token_parsed',
  REFRESH_TOKEN: 'fo:refresh_token',
  ID_TOKEN: 'fo:id_token',
});

/**
 * Keycloak service that handles communication for login / logout / token refresh
 */
class KeyCloakService {
  // user service client id
  static userService = 'user-service';

  // minimum token validity in seconds
  static minTokenValidity = 60 * 3;

  constructor() {
    this.keycloak = new Keycloak({
      url: process.env.REACT_APP_KEYCLOAK_URL || '/auth',
      realm: 'front-office-public',
      clientId: 'frontend',
    });

    this.updateTokenInterval = undefined;

    // Check token validity every 10 seconds (10 000 ms) and, if necessary, update the token.
    // Refresh token if it's valid for less than {minTokenValidity} seconds
    // The token should always be valid since we're refreshing it at least 1 minute earlier
    // This is why this function can run the _saveTokens async instead of awaiting
    this.keycloak.onAuthSuccess = () => {
      clearInterval(this.updateTokenInterval);
      this.updateTokenInterval = setInterval(async () => this.refreshToken(), 10000);
    };
    this.keycloak.onAuthError = () => {
      this.logout(window.location.href);
    };
    this.keycloak.onAuthRefreshError = () => {
      this.logout(window.location.href);
    };
  }

  /**
   * Performs keycloak login
   * @param options - options for keycloak login
   *    - 'idpHint' : identity provider hint used by keycloak
   *    - 'isCertificate' - flag that states login via certificate is being called
   * @returns {Promise<*|null>}
   */
  async login(options) {
    const { idpHint, isCertificate } = Object.assign({ idpHint: undefined, isCertificate: false }, options);
    const kcOptions = {
      token: localStorage.getItem(KeycloakStorageKeys.ACCESS_TOKEN) ?? this.keycloak?.token,
      refreshToken: localStorage.getItem(KeycloakStorageKeys.REFRESH_TOKEN) ?? this.keycloak?.refreshToken,
      idToken: localStorage.getItem(KeycloakStorageKeys.ID_TOKEN) ?? this.keycloak?.idToken,
      redirectUri: window.location.href.split('?')[0],
      ...((idpHint || isCertificate) && { onLoad: 'login-required' }),
    };

    if (idpHint) {
      const kcLogin = this.keycloak.login;
      this.keycloak.login = _options => kcLogin({ ..._options, idpHint });
    }

    const authenticated = await this.keycloak.init(kcOptions);
    if (kcOptions.token) {
      try {
        if (this.keycloak.isTokenExpired(KeyCloakService.minTokenValidity)) {
          await this.refreshToken();
        }
      } catch (error) {
        // do nothing
      }
    }

    if (!authenticated) return null;

    await this._saveTokens();

    return this._userInfo;
  }

  /**
   * Performs user logout
   * @param redirectUri - redirects the user to this URI after logout
   * @returns {Promise<void>}
   */
  async logout(redirectUri) {
    clearInterval(this.updateTokenInterval);
    this._clearTokens();
    await this.keycloak.logout({ redirectUri });
  }

  /**
   * Refresh the token when necessary (e.g. change organizations)
   * @param force - force token refresh by setting the minValidity to a very high number
   * @returns {Promise<unknown>}
   */
  async refreshToken({ force } = { force: false }) {
    return new Promise((resolve, reject) => {
      if (this.keycloak?.token) {
        const minValidity = force ? 9999999999 : KeyCloakService.minTokenValidity;
        this.keycloak
          .updateToken(minValidity)
          .then(async (refreshed) => {
            if (refreshed) await this._saveTokens();
            resolve(refreshed);
          })
          .catch(() => {
            this.logout(window.location.href);
            // eslint-disable-next-line prefer-promise-reject-errors
            reject(false);
          });
      }
    });
  }

  /**
   * Clears all tokens from the localStorage and the keycloak instance
   * Private function, please do not use externally
   */
  // eslint-disable-next-line class-methods-use-this
  _clearTokens() {
    Object.values(KeycloakStorageKeys).forEach(key => localStorage.removeItem(key));
  }

  /**
   * Saves all tokens to the localStorage
   * Private function, please do not use externally
   */
  async _saveTokens() {
    localStorage.setItem(KeycloakStorageKeys.ACCESS_TOKEN, this.keycloak.token);
    localStorage.setItem(KeycloakStorageKeys.ACCESS_TOKEN_PARSED, JSON.stringify(this.keycloak.tokenParsed));
    localStorage.setItem(KeycloakStorageKeys.REFRESH_TOKEN, this.keycloak.refreshToken);
    localStorage.setItem(KeycloakStorageKeys.ID_TOKEN, this.keycloak.idToken);

    return new Promise((resolve) => {
      // Create a new KeycloakAuthorization each time we save a new token to retrieve the uma token
      const keycloakAuthorization = new KeycloakAuthorization(this.keycloak);
      // Even though the init function is void, the uma configuration is loaded async via XMLHttpRequest
      keycloakAuthorization.init();
      // Because the configuration is loaded async (see above comment)
      // We're going to use a private 'ready' promise which tells us if the configuration has been retrieved or not.
      // eslint-disable-next-line no-unused-expressions
      if (keycloakAuthorization.hasOwnProperty('ready')) {
        keycloakAuthorization.ready
          .then(() => {
            keycloakAuthorization.entitlement(KeyCloakService.userService).then(
              (token) => {
                localStorage.setItem(KeycloakStorageKeys.UMA_ACCESS_TOKEN, token);
                localStorage.setItem(KeycloakStorageKeys.UMA_ACCESS_TOKEN_PARSED, JSON.stringify(parseJwt(token)));
                resolve(true);
              },
              () => resolve(true),
              () => resolve(true),
            );
          })
          .catch(() => resolve(true));
      }
    });
  }

  /**
   * Getter for user information
   * Private function, please do not use externally
   * @returns {*} - user information
   */
  get _userInfo() {
    const userServiceRoles = this.keycloak?.tokenParsed?.resource_access?.[KeyCloakService.userService]?.roles ?? [];
    const roles = userServiceRoles
      .filter(item => item.includes(this.keycloak.tokenParsed?.current_org_id))
      .map(role => role.split('/')[0]);

    const umaTokenParsed = JSON.parse(localStorage.getItem(KeycloakStorageKeys.UMA_ACCESS_TOKEN_PARSED));
    const extraUserServiceRoles = umaTokenParsed?.authorization?.permissions ?? [];

    /**
     * Extra roles are being used only for signers from a different organization
     * At the moment, 'roles' will not be used since this is only for signing
     *    rsname: Submission/{submission.id}/{submission.tenantId}
     * @type {{submissionId: *, roles: *, tenantId: *}[]}
     */
    const extraRoles = extraUserServiceRoles.map(_permission => ({
      roles: _permission?.scopes,
      type: _permission?.rsname?.split('/')?.[0],
      submissionId: _permission?.rsname?.split('/')?.[1],
      tenantId: _permission?.rsname?.split('/')?.[2],
    }));

    return {
      ...this.keycloak.idTokenParsed,
      ...(roles.length > 0 && { roles }),
      ...(extraRoles.length > 0 && { extraRoles }),
    };
  }

  /**
   * Retrieve the access token
   * @returns {string} - access token (either uma or normal)
   */
  static getAccessToken() {
    const accessToken = localStorage.getItem(KeycloakStorageKeys.UMA_ACCESS_TOKEN);
    if (accessToken) return accessToken;
    return localStorage.getItem(KeycloakStorageKeys.ACCESS_TOKEN);
  }

  /**
   * Retrive the parsed access token
   * @returns {any|null}
   */
  static getAccessTokenParsed() {
    const accessTokenParsedString = localStorage.getItem(KeycloakStorageKeys.ACCESS_TOKEN_PARSED);
    if (!accessTokenParsedString) return null;
    return JSON.parse(accessTokenParsedString);
  }
}

export const keycloakService = Object.preventExtensions(new KeyCloakService());
export const { getAccessToken, getAccessTokenParsed } = KeyCloakService;
